import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { NotificationService } from '../../../../services/notifications.service';
import { BaseService } from '../../../../services/base.service';
import { ChannelService, initItemsParams } from '../../../../services/channel.service';
import { MatDialog } from '@angular/material/dialog';
import { IntegrationService } from '../../../../services/integration.service';
import { ModalService } from '../../../../services/modal.service';
import { AppTableSource, ColumnSortType } from '../../../../model/table.model';
import { tableHeaders } from './my-backups.interface';
import { IParams } from '../../../../model/http';
import { OlxAdvertService } from '../../../../services/olx-advert.service';
import { SelectionModel } from '@angular/cdk/collections';
import { parseCurrencyXXX } from '../../../../helpers';
import { DialogService } from '../../../../services/dialog.service';
import { IError } from '../../../../interfaces';
import { CHANNEL_TYPE, DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations, MODAL_TYPES } from '../../../../enum/constants';
import { ActivatedRoute } from '@angular/router';
import { PlannerComponent } from '../../../../modals/planner/planner.component';

@Component({
  selector: 'app-my-backups',
  templateUrl: './my-backups.component.html',
  styleUrls: ['./my-backups.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MyBackupsComponent implements OnInit, OnDestroy {
  // Destroy relative
  @Output() onReloadChat = new EventEmitter();

  private readonly destroy$: Subject<null> = new Subject<null>();
  dataSource$: Observable<AppTableSource> = this.olxAdvertService.items$.pipe(
    map((data) => {
      tableHeaders.map((column) => {
        column.sort = ColumnSortType.NONE;
        if (column.value === this.dataSort?.active) {
          column.sort = this.dataSort.direction;
        }
        return column;
      });
      return {
        headers: tableHeaders,
        rows: data.items?.map((v: any) => {
          return {
            ...v,
            price_value: parseCurrencyXXX(v.price?.value, v.price?.currency),
            contact_name: v.contact?.name,
            contact_phone: v.contact?.phone,
          };
        }),
        pageSize: data?.size || 0,
        pageIndex: data.page ? data.page - 1 : 0,
        length: data.total || 0,
      };
    }),
  );
  visibleColumns = tableHeaders.map((v) => v.value);
  selectionTable = new SelectionModel<any>(true, []);

  public bulkActionList: any[] = [{ label: 'restore', value: 'PLANNER' }];

  private itemsRequest: any;
  private dataSort: any;
  public isLoader: boolean = false;

  public items: any[] = [];
  public currentChannelId: any = null;
  public balance: any;
  private channelQuery = this.activatedRoute.snapshot.queryParams['channel'];

  constructor(
    private readonly notificationService: NotificationService,
    private readonly integrationService: IntegrationService,
    public readonly baseService: BaseService,
    private readonly olxAdvertService: OlxAdvertService,
    private readonly dialog: MatDialog,
    private readonly modalService: ModalService,
    private readonly dialogService: DialogService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly channelService: ChannelService,
  ) {}

  getItemsWithUpdatedParams(params: IParams, isLoader = false) {
    if (this.itemsRequest) {
      this.itemsRequest.unsubscribe();
    }
    this.isLoader = isLoader;
    this.selectionTable.clear();
    this.itemsRequest = this.olxAdvertService
      .setQueryBackupsParams(this.currentChannelId, params)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (res) => {
          this.isLoader = false;
          // this.cdr.detectChanges();

          // this.loadPlanners(res.items.map((v: any) => v.id));
          if (!res.items.length && res.page > 1) {
            this.getItemsWithUpdatedParams(
              {
                page: res.page - 1,
                take: res.take,
              },
              true,
            );
          }
        },
        error: () => {
          this.isLoader = false;
          // this.cdr.detectChanges();
        },
      });
  }

  ngOnInit() {
    this.isLoader = true;
    this.channelService
      .getChannels({ scope: 'apps', types: [CHANNEL_TYPE.OLX] })
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (channels: any[]) => {
          this.items = channels;
          const data = {
            id: this.channelQuery || channels[0]?.id || null,
          };
          this.onValueChange(data);
        },
        error: () => {
          this.isLoader = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.olxAdvertService.destroy();
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  onSortChange(sortState: any) {
    this.dataSort = sortState;
    this.getItemsWithUpdatedParams({
      sort: sortState?.active,
      order: sortState.direction,
    });
  }

  onPaginationChange(paginationDetails: any) {
    const { pageIndex, pageSize } = paginationDetails;
    const page = pageIndex + 1;
    const take = pageSize;
    this.getItemsWithUpdatedParams({ page, take }, true);
  }

  onActionChange(event: any) {
    if (event.label === 'RESTORE_ADVERT') {
      this.olxAdvertService
        .backupRestore(this.currentChannelId, event.crm_id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.getItemsWithUpdatedParams({});
          },
        });
    }
  }

  timerSearch: any;

  onSearchChange(search: string) {
    if (this.timerSearch) clearTimeout(this.timerSearch);
    this.timerSearch = setTimeout(() => {
      this.getItemsWithUpdatedParams(
        {
          ...initItemsParams,
          search,
        },
        true,
      );
    }, 500);
  }

  onValueChange(event: any) {
    this.currentChannelId = event?.id;
    this.baseService.setQuery('channel', this.currentChannelId);
    this.initBalance();
    this.getItemsWithUpdatedParams({}, true);
  }

  bulkActionPopup(event: any) {
    const ids: string[] = this.selectionTable.selected.filter((v) => v.id && !v?.planned_count).map((v) => v.id);

    const allIds: string[] = this.selectionTable.selected.map((v) => v.id);

    if (event.value === 'ACTIVE_NOW') {
      this.olxAdvertService
        .updateBulkStatus(this.currentChannelId, ids, 'activate')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.notificationService.showWarning(
              '',
              activeTranslations.notifications_texts.warn.submitted_for_activation_2,
            );
            this.getItemsWithUpdatedParams({});
          },
        });
    }

    if (event.value === 'DEACTIVATE_NOW') {
      this.olxAdvertService
        .updateBulkStatus(this.currentChannelId, ids, 'deactivate')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.notificationService.showWarning(
              '',
              activeTranslations.notifications_texts.warn.submitted_for_deactivation_2,
            );
            this.getItemsWithUpdatedParams({});
          },
        });
    }

    if (event.value === 'PLANNER') {
      if (ids.length) {
        if (ids.length >= 2) {
          this.notificationService.showError(
            activeTranslations.notifications_texts.warn.warn,
            activeTranslations.notifications_texts.error.mass_activation_of_ads,
          );
        }
        this.modalService
          .openModal({
            title: 'ads_activation_time',
            translate: true,
            component: PlannerComponent,
            data: {
              modalType: MODAL_TYPES.ADVERT,
              adverts: allIds,
              channel_id: this.currentChannelId,
            },
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.getItemsWithUpdatedParams({});
          });
      }
    }
    if (event.value === 'PUSH_UP') {
      const allStatus = this.selectionTable.selected.map((v) => v.status);
      if (allStatus.includes(event.status)) {
        this.notificationService.showWarning(
          '',
          activeTranslations.notifications_texts.warn.possible_for_active_ads,
        );
      } else {
        this.olxAdvertService
          .bulkPushUpAdvert(this.currentChannelId, allIds)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.getItemsWithUpdatedParams({});
              this.initBalance();
            },
            error: (e) => {
              (e.error?.errors || []).forEach((err: IError) => {
                if (err.code === 'EXTERNAL_ERROR') {
                  this.notificationService.showError(
                    '',
                    activeTranslations.errors.redo_the_ad,
                  );
                } else {
                  this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
                }
              });
            },
          });
      }
    }
  }

  onFilterChange(event: string, item: any) {
    this.getItemsWithUpdatedParams({
      filters: { status: item?.value },
    });
  }

  public initBalance() {
    this.olxAdvertService
      .getBalance(this.currentChannelId)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (balance) => {
          this.balance = balance;
        },
        error: () => {},
      });
  }
}
